import React from 'react';
import { styled } from '@mui/material';
import { Colors } from 'library';
import { Link } from 'react-router-dom';
import { BaseButton } from './BaseButton';

interface PillButtonProps {
    children: React.ReactNode;
    onClick?: () => void;
    to?: string;
    href?: string;
    variant?: 'primary' | 'secondary';
    icon?: React.ReactNode;
    disabled?: boolean;
    color?: string;
    hoverColor?: string;
}

export function PillButton({
    children,
    onClick,
    to,
    href,
    variant = 'primary',
    icon,
    disabled,
    color,
}: PillButtonProps) {
    const isPrimary = variant === 'primary';

    const colorStyle = color ? (isPrimary ? { backgroundColor: color } : { borderColor: color, color }) : undefined;

    if (href && !disabled) {
        const AnchorComponent = isPrimary ? PrimaryPillAnchor : SecondaryPillAnchor;
        return (
            <AnchorComponent href={href} style={colorStyle}>
                {icon}
                {children}
            </AnchorComponent>
        );
    }

    if (to && !disabled) {
        const LinkComponent = isPrimary ? PrimaryPillLink : SecondaryPillLink;
        return (
            <LinkComponent to={to} style={colorStyle}>
                {icon}
                {children}
            </LinkComponent>
        );
    }

    const Button = isPrimary ? PrimaryPill : SecondaryPill;
    return (
        <Button onClick={onClick} disabled={disabled} style={colorStyle}>
            {icon}
            {children}
        </Button>
    );
}

const basePill = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    padding: '8px 24px',
    borderRadius: 24,
    fontSize: 14,
    fontWeight: 600,
    whiteSpace: 'nowrap' as const,
    textDecoration: 'none',
};

const PrimaryPill = styled(BaseButton)({
    ...basePill,
    backgroundColor: Colors.darkNavy,
    color: Colors.white,
    '&:hover:not(:disabled)': {
        backgroundColor: Colors.primaryDarkened,
        color: Colors.white,
    },
});

const SecondaryPill = styled(BaseButton)({
    ...basePill,
    border: `1px solid ${Colors.lightGrey}`,
    backgroundColor: Colors.white,
    color: Colors.darkNavy,
    '&:hover:not(:disabled)': {
        backgroundColor: Colors.fill,
    },
});

const PrimaryPillLink = styled(Link)({
    ...basePill,
    border: 'none',
    backgroundColor: Colors.darkNavy,
    color: Colors.white,
    '&:hover': {
        backgroundColor: Colors.primaryDarkened,
        color: Colors.white,
        textDecoration: 'none',
    },
});

const SecondaryPillLink = styled(Link)({
    ...basePill,
    border: `1px solid ${Colors.lightGrey}`,
    backgroundColor: Colors.white,
    color: Colors.darkNavy,
    '&:hover': {
        backgroundColor: Colors.fill,
        textDecoration: 'none',
    },
});

const PrimaryPillAnchor = styled('a')({
    ...basePill,
    border: 'none',
    backgroundColor: Colors.darkNavy,
    color: Colors.white,
    '&:hover': {
        backgroundColor: Colors.primaryDarkened,
        color: Colors.white,
        textDecoration: 'none',
    },
});

const SecondaryPillAnchor = styled('a')({
    ...basePill,
    border: `1px solid ${Colors.lightGrey}`,
    backgroundColor: Colors.white,
    color: Colors.darkNavy,
    '&:hover': {
        backgroundColor: Colors.fill,
        color: Colors.darkNavy,
        textDecoration: 'none',
    },
});
